import React from "react";
import * as PropTypes from "prop-types";
import * as Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import styleConstants from "../../styleConstants";
import MirrorBarChartHeader from "./MirroredBarChartHeader";

const overlayBarWidth = 2;

const getMaximumValue = series =>
  Math.max(...series.flatMap(item => item.data.map(value => Math.abs(value))));

const MirroredBarChart = props => {
  const maximumValue = props.absolute ? getMaximumValue(props.series) : 100;

  const negativeSeriesValues = props.series[0].data.map(y => Math.abs(y));
  const positiveSeriesValues = props.series[1].data;
  const positiveSeriesProportions = positiveSeriesValues.map(
    (value, index) => value / (value + negativeSeriesValues[index])
  );

  const proportionSeries = props.series.map(series => ({
    ...series,
    data: series.data.map((y, index) => {
      const positiveSeriesProportion = positiveSeriesProportions[index];

      return {
        y,
        proportion:
          y >= 0 ? positiveSeriesProportion : 1 - positiveSeriesProportion
      };
    })
  }));

  let options = {
    chart: {
      type: "bar",
      height: props.height,
      backgroundColor: null,
      style: {
        fontFamily: "'Source Sans Pro', sans-serif;"
      }
    },
    title: {
      text: null
    },
    plotOptions: {
      bar: {
        grouping: false,
        groupPadding: 0.1
      },
      series: {
        stacking: !props.absolute ? "percent" : "normal",
        dataLabels: {
          enabled: props.showDataLabels
        }
      }
    },
    xAxis: {
      reversed: false,
      categories: props.categories,
      labels: {
        step: 1,
        style: {
          fontSize: "0.875rem",
          color: "black"
        }
      },
      lineWidth: 0,
      gridLineWidth: 1,
      gridLineDashStyle: "shortDot",
      gridLineColor: styleConstants.colors.gray4,
      tickmarkPlacement: "on"
    },
    yAxis: {
      offset: 10,
      title: {
        text: props.yAxisTitle
      },
      min: -maximumValue,
      max: maximumValue,
      labels: {
        formatter: function() {
          const defaultLabelFormatter = this.axis.defaultLabelFormatter;

          return props.absolute
            ? defaultLabelFormatter.call({
                axis: this.axis,
                value: Math.abs(this.value)
              })
            : `${Math.abs(this.value)}%`;
        },
        style: {
          fontSize: "0.875rem",
          color: styleConstants.colors.gray4
        }
      },
      reversedStacks: props.reversedStacks,
      lineWidth: 1,
      lineColor: styleConstants.colors.gray4,
      tickPosition: "inside",
      tickLength: 5,
      tickWidth: 1,
      tickColor: styleConstants.colors.gray4,
      gridLineWidth: 0
    },
    series: [
      ...proportionSeries.map(series => ({
        ...series,
        pointWidth: series.overlay ? overlayBarWidth : undefined
      }))
    ],
    tooltip: {
      backgroundColor: "white",
      useHTML: true,
      outside: true,
      formatter: props.tooltipFormatter,
      style: {
        fontSize: "0.875rem",
        color: styleConstants.colors.gray6
      },
      enabled: !!props.tooltipFormatter
    },
    exporting: {
      enabled: false
    },
    credits: {
      enabled: false
    }
  };

  switch (props.legendPosition) {
    case "none":
      options = {
        ...options,
        legend: {
          enabled: false
        }
      };

      break;

    case "right":
      options = {
        ...options,
        legend: {
          layout: "vertical",
          align: "right",
          verticalAlign: "middle"
        }
      };

      break;

    case "top":
      options = {
        ...options,
        legend: {
          verticalAlign: "top"
        }
      };

      break;

    default:
      break;
  }

  return (
    <div>
      <MirrorBarChartHeader
        offset={props.headerOffset}
        leftSeries={props.series[0].name}
        rightSeries={props.series[1].name}
      />
      <HighchartsReact
        ref={props.innerRef}
        highcharts={Highcharts}
        options={options}
      />
    </div>
  );
};

MirroredBarChart.propTypes = {
  height: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  headerOffset: PropTypes.number,
  yAxisTitle: PropTypes.string,
  categories: PropTypes.arrayOf(PropTypes.string).isRequired,
  series: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      data: PropTypes.arrayOf(PropTypes.number).isRequired,
      stack: PropTypes.string,
      overlay: PropTypes.bool
    })
  ).isRequired,
  reversedStacks: PropTypes.bool,
  legendPosition: PropTypes.oneOf(["none", "bottom", "right", "top"]),
  showDataLabels: PropTypes.bool,
  absolute: PropTypes.bool,
  tooltipFormatter: PropTypes.func
};

MirroredBarChart.defaultProps = {
  height: "100%",
  reversedStacks: false,
  legendPosition: "bottom",
  showDataLabels: false,
  tooltipFormatter: function(tooltip) {
    return tooltip.defaultFormatter.call(this, tooltip);
  }
};

export default React.forwardRef((props, ref) => (
  <MirroredBarChart innerRef={ref} {...props} />
));
